import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { toast } from "sonner";
import { List, SignOut, UserCircle, ShieldCheck } from "@phosphor-icons/react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { BaseCurrencySelect } from "./BaseCurrencySelect";
import { InstallButton, InstallBanner } from "./InstallButton";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export function AppHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // poll alerts for in-app notifications
  useEffect(() => {
    if (!user) return;
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/alerts/check");
        if (active && data.triggered?.length) {
          data.triggered.forEach((a) => {
            const val = a.asset_type === "crypto" ? `$${a.current_value}` : a.current_value;
            toast.success(`Alert: ${a.symbol}`, {
              description: `${a.alert_type === "pct_change" ? `Moved ${a.current_change?.toFixed(2)}%` : `Now ${val}`} (target ${a.target_value})`,
              duration: 8000,
            });
          });
        }
      } catch {}
    };
    check();
    const id = setInterval(check, 60000);
    return () => { active = false; clearInterval(id); };
  }, [user]);

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <>
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <button onClick={() => nav("/")} data-testid="header-home" className="shrink-0">
          <Logo />
        </button>
        <div className="flex items-center gap-2">
          <InstallButton />
          <BaseCurrencySelect />
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="inline-flex h-9 items-center gap-2 border border-border bg-card px-3 hover-lift text-sm">
                <UserCircle size={18} weight="bold" />
                <span className="hidden sm:inline max-w-[140px] truncate">{user?.email}</span>
                <List size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account">
                <UserCircle size={16} className="mr-2" /> Account
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                  <ShieldCheck size={16} className="mr-2" /> Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-danger">
                <SignOut size={16} className="mr-2" /> Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
    <InstallBanner />
    </>
  );
}
